import { useCallback } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { ExternalShippingCarrierAddress } from "@jti/commerce-types";
import type { AddressStored } from "@jti/fe-common";
import type { FlotiqInStoreDeliveryStore } from "@jti/fe-common/types";
import { useFormContext } from "@jti/ui";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { shippingMethodFieldFactory, telephoneFieldFactory } from "@checkout-v2/formFields";
import type { ShippingFormFieldsConfig } from "@checkout-v2/forms";
import { useSetShippingAddressOnCart } from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";
import type { RawAddressWithNoContactInfo } from "@checkout-v2/shared/types/RawAddressWithNoContactInfo.types";
import { getMagentoAddress } from "@checkout-v2/shared/utils/getMagentoAddress";

type ApplicableAddresses = AddressStored | FlotiqInStoreDeliveryStore | ExternalShippingCarrierAddress;

export const useOnShippingAddressChange = (formFieldsConfig: ShippingFormFieldsConfig) => {
  const { __ } = useTranslations();
  const { setShippingAddressOnCart } = useSetShippingAddressOnCart();
  const { ISO_COUNTRY_CODE } = useConfig();
  const checkoutConfig = useCheckoutV2Configuration();
  const { watch } = useFormContext();

  const selectedShippingMethodCode = watch(formFieldsConfig.shippingMethodCode.name);

  const firstname = watch(formFieldsConfig.firstName.name);
  const lastname = watch(formFieldsConfig.lastName.name);
  const telephoneFieldValue = watch(formFieldsConfig.telephone.name);
  const telephone = telephoneFieldFactory.toTelephoneString(
    telephoneFieldValue,
    formFieldsConfig.telephone.savedDefaultValue
  );

  const enrichAddressWithContactInfo = useCallback(
    (magentoAddress: RawAddressWithNoContactInfo) => ({
      ...magentoAddress,
      telephone,
      firstname,
      lastname,
    }),
    [telephone, firstname, lastname]
  );

  const onShippingAddressChange = useCallback(
    async (address: ApplicableAddresses) => {
      const methodType = shippingMethodFieldFactory.getSelectedShippingMethodType(
        selectedShippingMethodCode,
        checkoutConfig
      );

      const magentoAddress = getMagentoAddress({ methodType, address, countryCode: ISO_COUNTRY_CODE });

      if (!magentoAddress) {
        throw new Error(__("cart.checkout.shipping_form.error.shipping_address.wrong_address"));
      }

      const addressWithContactInfo = enrichAddressWithContactInfo(magentoAddress);

      return await setShippingAddressOnCart(addressWithContactInfo);
    },
    [
      selectedShippingMethodCode,
      checkoutConfig,
      ISO_COUNTRY_CODE,
      setShippingAddressOnCart,
      enrichAddressWithContactInfo,
      __,
    ]
  );

  return { onShippingAddressChange };
};
